import React from "react";
import { Box, Typography, SvgIcon } from "@mui/material";

interface LogoProps {
  size?: "small" | "medium" | "large";
  showText?: boolean;
  variant?: "horizontal" | "vertical";
  color?: "primary" | "white" | "dark";
}

const Logo: React.FC<LogoProps> = ({
  size = "medium",
  showText = true,
  variant = "horizontal",
  color = "primary",
}) => {
  const sizeMap = {
    small: { icon: 24, text: "body2" },
    medium: { icon: 32, text: "h6" },
    large: { icon: 48, text: "h4" },
  };

  const colorMap = {
    primary: {
      keep: "#2196F3",
      kid: "#E91E63",
      busy: "#2196F3",
    },
    white: {
      keep: "#FFFFFF",
      kid: "#FFFFFF",
      busy: "#FFFFFF",
    },
    dark: {
      keep: "#1976D2",
      kid: "#C2185B",
      busy: "#1976D2",
    },
  };

  const currentSize = sizeMap[size];
  const currentColors = colorMap[color];

  const LogoIcon = () => (
    <SvgIcon
      sx={{
        fontSize: currentSize.icon,
        filter: color === "white" ? "brightness(0) invert(1)" : "none",
      }}
      viewBox="0 0 100 100"
    >
      {/* Bottom circle with gradient (pink to purple) */}
      <defs>
        <linearGradient id="pinkPurple" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" style={{ stopColor: "#E91E63", stopOpacity: 1 }} />
          <stop
            offset="100%"
            style={{ stopColor: "#9C27B0", stopOpacity: 1 }}
          />
        </linearGradient>
        <linearGradient id="yellowOrange" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" style={{ stopColor: "#FFD93D", stopOpacity: 1 }} />
          <stop
            offset="100%"
            style={{ stopColor: "#FF8E53", stopOpacity: 1 }}
          />
        </linearGradient>
      </defs>

      {/* Bottom circle (pink to purple) */}
      <circle cx="50" cy="65" r="35" fill="url(#pinkPurple)" />

      {/* Top cap/sun (yellow to orange) */}
      <path d="M 15 65 Q 50 30 85 65" fill="url(#yellowOrange)" stroke="none" />

      {/* Smiley face */}
      <circle cx="40" cy="60" r="3" fill="white" />
      <circle cx="60" cy="60" r="3" fill="white" />
      <path
        d="M 35 70 Q 50 80 65 70"
        stroke="white"
        strokeWidth="3"
        fill="none"
        strokeLinecap="round"
      />
    </SvgIcon>
  );

  if (!showText) {
    return <LogoIcon />;
  }

  if (variant === "vertical") {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
        }}
      >
        <LogoIcon />
        <Box sx={{ textAlign: "center" }}>
          <Typography
            variant={currentSize.text as any}
            sx={{
              fontWeight: 700,
              color: currentColors.keep,
              lineHeight: 1,
            }}
          >
            Keep
          </Typography>
          <Typography
            variant={currentSize.text as any}
            sx={{
              fontWeight: 700,
              color: currentColors.kid,
              lineHeight: 1,
            }}
          >
            Kid
          </Typography>
          <Typography
            variant={currentSize.text as any}
            sx={{
              fontWeight: 700,
              color: currentColors.busy,
              lineHeight: 1,
            }}
          >
            Busy
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <LogoIcon />
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Typography
          variant={currentSize.text as any}
          sx={{
            fontWeight: 700,
            color: currentColors.keep,
            lineHeight: 1,
          }}
        >
          Keep
        </Typography>
        <Typography
          variant={currentSize.text as any}
          sx={{
            fontWeight: 700,
            color: currentColors.kid,
            lineHeight: 1,
          }}
        >
          Kid
        </Typography>
        <Typography
          variant={currentSize.text as any}
          sx={{
            fontWeight: 700,
            color: currentColors.busy,
            lineHeight: 1,
          }}
        >
          Busy
        </Typography>
      </Box>
    </Box>
  );
};

export default Logo;
